"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreemAuthTest = void 0;
const logger_1 = require("@streem/logger");
const toolbox_1 = require("@streem/toolbox");
const errors_1 = require("./errors");
const access_token_manager_1 = require("./managers/access_token_manager");
const oauth_manager_1 = require("./managers/oauth_manager");
const guards_1 = require("./types/guards");
const company_user_1 = require("./utils/company_user");
const identified_user_1 = require("./utils/identified_user");
const jwt_token_1 = require("./utils/jwt_token");
const invite_user_1 = require("./utils/invite_user");
const logger_2 = require("./utils/logger");
const sdk_user_1 = require("./utils/sdk_user");
const mobx_1 = require("mobx");
class StreemAuth {
    constructor() {
        this.initialized = false;
        this.configCache = new Map();
        this.userLoggedOut = false;
        this.authCallbacks = new Set();
        this.disposers = [];
        this.tokenFetcher = {
            isTokenAvailable: () => {
                return this.authenticated;
            },
            fetchAccessToken: () => __awaiter(this, void 0, void 0, function* () {
                return yield this.fetchBearerToken();
            }),
            refreshAccessToken: () => __awaiter(this, void 0, void 0, function* () {
                yield this.refreshAccessToken();
            }),
        };
    }
    configure(config) {
        (0, logger_2.setLogger)(config.logger);
        this.centralLoginAppName = config.centralLoginAppName;
        this.regionalApiUrl = config.regionalApiUrl;
        this.globalApiUrl = config.globalApiUrl;
        this.accessTokenManager = new access_token_manager_1.AccessTokenManager(this.regionalApiUrl, config.appId);
        this.oauthManager = new oauth_manager_1.OAuthManager();
        // Kickoff the async initialization
        logger_2.logger.info('Kicking off the async initialization of StreemAuth');
        this.initialize();
    }
    // the offlineAccess flag enables long-lived single use refresh tokens
    loginWithOauthRedirect(companyCode, offlineAccess = true) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.oauthManager, 'StreemAuth.configure has not been called');
            const config = companyCode === this.centralLoginAppName
                ? yield this.getCentralLoginConfig()
                : yield this.getLoginConfig(companyCode);
            yield this.oauthManager.performAuthorizationRequest(config, offlineAccess);
        });
    }
    loginWithInviteCode(inviteCode) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.accessTokenManager, 'StreemAuth.configure has not been called');
            // Returns access token after exchanging with invite code.
            const credentials = yield this.accessTokenManager.invitationLogin(inviteCode);
            this._accessToken = credentials.access_token;
            const user = yield (0, invite_user_1.getInvitedUserFromCredentials)(credentials);
            this.setAuthUser(user);
            return user;
        });
    }
    loginWithSDKToken(sdkToken, companyCode) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.accessTokenManager, 'StreemAuth.configure has not been called');
            const credentials = yield this.accessTokenManager.fetch(sdkToken, companyCode);
            this._accessToken = credentials.access_token;
            const user = yield (0, sdk_user_1.getSDKUserFromCredentials)(credentials, companyCode);
            this.setAuthUser(user);
            return user;
        });
    }
    loginSDKUserWithAccessToken(companyCode) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.accessTokenManager, 'StreemAuth.configure has not been called');
            const credentials = yield this.accessTokenManager.getExistingTokenIfValid();
            (0, toolbox_1.invariant)(credentials, 'No valid access token found');
            this._accessToken = credentials.access_token;
            const user = yield (0, sdk_user_1.getSDKUserFromCredentials)(credentials, companyCode);
            this.setAuthUser(user);
            return user;
        });
    }
    /**
     * Logs the current authUser, if one exists, out.
     *
     * The logout action can take one of two paths depending on whether the company is configured with OAuth or not. If
     * the company is using OAuth, then we clear the id and access tokens before redirecting the browser to the SSO
     * logout URL. Otherwise we trigger any onAuthStateChanged callbacks, providing undefined as the user parameter.
     *
     * @param forceWithCompanyCode Force the logout even if the authUser is undefied by providing a companyCode.
     */
    logout(forceWithCompanyCode = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this.clearStorage();
            this.userLoggedOut = true;
            if ((0, guards_1.isExpertUser)(this._authUser) || forceWithCompanyCode !== false) {
                const config = yield this.getLoginConfig((0, guards_1.isExpertUser)(this._authUser) && forceWithCompanyCode === false
                    ? this._authUser.companyCode
                    : forceWithCompanyCode);
                this.redirectToOAuthLogoutEndpoint(config);
            }
            else if ((0, guards_1.isLoginIdentityUser)(this._authUser)) {
                const config = yield this.getCentralLoginConfig();
                this.redirectToOAuthLogoutEndpoint(config);
            }
            else {
                this.resetState();
            }
        });
    }
    /**
     * Call any disposer functions and clear the disposers array.
     */
    cleanupDisposers() {
        this.disposers = this.disposers.filter(disposer => {
            disposer();
            return false;
        });
    }
    /**
     * Register a callback that will be invoked each time the current user state changes. If both callback parameters
     * are undefined, it's safe to assume the user is logged out. If the AnyUser is undefined and StreemAuthError is
     * defined, we encountered an error logging in or out.
     *
     * @param callback A callback that receives an optional AnyUser and/or StreemAuthError.
     */
    onAuthStateChanged(callback) {
        this.authCallbacks.add(callback);
        // NOTE: there is existing code that uses this function but does not call the returned function to do cleanup.
        // some of that existing code is in React hooks that fire back-to-back quickly causing multiple `autorun` subscriptions
        // so we need to ensure we cleanup the disposers to prevent memory leaks. Choosing to handle this internally rather than at every call site.
        this.cleanupDisposers();
        const disposer = (0, mobx_1.autorun)(() => {
            if (this.initialized && this.latestAuthResponse) {
                callback(...this.latestAuthResponse);
            }
            else if (this.initialized) {
                callback(this._authUser, undefined);
            }
        });
        this.disposers.push(disposer);
        return () => {
            this.cleanupDisposers();
            return this.authCallbacks.delete(callback);
        };
    }
    /**
     * Returns true if we are able to reliably produce an access or id token for making API requests.
     */
    get authenticated() {
        return ((0, guards_1.isExpertUser)(this._authUser) ||
            this._idToken !== undefined ||
            this._accessToken !== undefined);
    }
    /**
     * Returns either an access token for ExpertUser or id token for InvitedUser or LoginIdentityUser. Will throw an
     * UnauthenticatedError if no user is authenticated. It is recommended to call `authenticated()` prior to calling
     * this method to ensure a token is returned successfully.
     */
    fetchBearerToken() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.accessTokenManager, 'StreemAuth.configure has not been called');
            (0, toolbox_1.invariant)(this.userLoggedOut === false, 'Cannot fetch access token since user has already logged out.');
            const validAccessToken = this.accessTokenManager.getExistingTokenIfValid();
            if (validAccessToken) {
                return validAccessToken.access_token;
            }
            if (this._authUser && !(0, guards_1.isLoginIdentityUser)(this._authUser)) {
                if ((0, jwt_token_1.validJwtToken)(this._idToken)) {
                    return (yield this.accessTokenManager.fetch(this._idToken, this._authUser.companyCode)).access_token;
                }
                else {
                    return (yield this.accessTokenManager.refresh(this._authUser.companyCode))
                        .access_token;
                }
            }
            if (!this._idToken) {
                throw new errors_1.UnauthenticatedError('You must first authenticate before fetching an access token.', 401);
            }
            (0, toolbox_1.invariant)((0, guards_1.isLoginIdentityUser)(this._authUser), 'Unexpected state in StreemAuth');
            return this._idToken;
        });
    }
    /**
     * Manually trigger a re-fetch of our platform API credentials, store them, and return the latest access token.
     * This function only works with ExpertUser types, it is the caller's responsibility to check the current user.
     */
    refreshAccessToken() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.accessTokenManager, 'StreemAuth.configure has not been called');
            const credentials = yield this.accessTokenManager.refresh((_a = this._authUser) === null || _a === void 0 ? void 0 : _a.companyCode);
            return credentials.access_token;
        });
    }
    /**
     * Clear the user's auth state and trigger any oauth callbacks
     * Usage is for browser sessions that are open but are not the user's current active session
     * Ie the user logged out from another tab, but we want the app state removal to propagate here too
     */
    completeIframeLogout() {
        this.resetState();
    }
    /**
     * Compares whether a given storage key is the same as the refresh token storage key
     *
     * @param storageKey the key to be compared
     * @returns true or false
     */
    keyMatchesRefreshTokenStorageKey(storageKey) {
        return storageKey === oauth_manager_1.REFRESH_TOKEN_STORAGE_KEY;
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.oauthManager && this.accessTokenManager, 'StreemAuth.configure has not been called');
            // Bail on non-browsers
            if (typeof window === 'undefined') {
                this.initialized = true;
                return;
            }
            // First check if we have a pending OAuth authorization request.
            if (this.oauthManager.pendingAuthorizationRequest()) {
                logger_2.logger.info('Found pending OAuth request, completing OAuth flow.');
                try {
                    const tokenResponse = yield this.oauthManager.completeAuthorizationRequest();
                    if (tokenResponse) {
                        yield this.setUserFromTokenResponse(tokenResponse);
                        logger_2.logger.info('Successfully authenticated via OAuth.');
                    }
                }
                catch (error) {
                    logger_2.logger.error('Error logging in via OAuth.', error);
                    this.resetState(error);
                }
                finally {
                    this.initialized = true;
                }
                return;
            }
            // Second check if we have a OAuth refresh token available.
            if (this.oauthManager.refreshTokenAvailable()) {
                logger_2.logger.info('Found OAuth refresh token, using to fetch id token.');
                try {
                    const tokenResponse = yield this.oauthManager.authorizeWithRefreshToken();
                    if (tokenResponse) {
                        yield this.setUserFromTokenResponse(tokenResponse);
                        logger_2.logger.info('Successfully authenticated with OAuth refresh token.');
                    }
                }
                catch (error) {
                    logger_2.logger.error('Error logging in via OAuth refresh token.', error);
                    // Refresh token failed, so clear storage and reload the page to reinitialize
                    this.clearStorage();
                    this.resetState(error);
                    window.location.reload();
                }
                finally {
                    this.initialized = true;
                }
                return;
            }
            // OauthManager found no token, set the default "logged out" state.
            logger_2.logger.debug('No id token found, user is not logged in.');
            this.resetState();
            this.initialized = true;
        });
    }
    resetState(error) {
        this.setAuthUser(undefined, error);
        this._idToken = undefined;
        this._accessToken = undefined;
    }
    clearStorage() {
        (0, toolbox_1.invariant)(this.oauthManager && this.accessTokenManager, 'StreemAuth.configure has not been called');
        this.oauthManager.clearStorage();
        this.accessTokenManager.clearStorage();
    }
    /**
     * Directs OAuth companies to logout from Streem on their side.
     *
     * Implies `this.resetState()` due to the browser refreshing
     */
    redirectToOAuthLogoutEndpoint(config) {
        var _a;
        /* eslint-disable camelcase */
        const loginConfig = config.login_configuration;
        const logoutEndpoint = loginConfig === null || loginConfig === void 0 ? void 0 : loginConfig.logout_endpoint;
        const clientId = (_a = loginConfig === null || loginConfig === void 0 ? void 0 : loginConfig.open_id) === null || _a === void 0 ? void 0 : _a.client_id;
        if (logoutEndpoint && clientId) {
            const logoutParams = new URLSearchParams({
                client_id: clientId,
                post_logout_redirect_uri: oauth_manager_1.REDIRECT_URI,
            });
            window.location.assign(`${logoutEndpoint}?${logoutParams}`);
        }
        /* eslint-enable camelcase */
    }
    setUserFromTokenResponse(tokenResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.accessTokenManager, 'StreemAuth.configure has not been called');
            (0, toolbox_1.invariant)(tokenResponse.idToken, 'setUserFromTokenResponse called with a response that did not contain an idToken');
            // Set the idToken so we're able to use it internally without broadcasting it.
            this._idToken = tokenResponse.idToken;
            // Use the type of idToken to set the correct user type.
            if ((0, company_user_1.isCompanyUserToken)(tokenResponse.idToken)) {
                // only reach out for access token if it's a company user and not an invite token
                const oauthCredentials = yield this.accessTokenManager.fetch(tokenResponse.idToken, (0, company_user_1.getCompanyCodeFromIdToken)(tokenResponse.idToken));
                const companyUser = (0, company_user_1.getCompanyUserFromToken)(tokenResponse.idToken, oauthCredentials);
                this.setAuthUser(companyUser);
            }
            else {
                const loginIdentity = (0, identified_user_1.getLoginIdentityFromToken)(tokenResponse.idToken);
                this.setAuthUser(loginIdentity);
            }
        });
    }
    /**
     * Set authUser and trigger onAuthStateChanged callbacks
     *
     * @param user Some form of Streem user, either identity, company, or invite.
     * @param error
     */
    setAuthUser(user, error) {
        // User Logging in
        if (user) {
            // resetting to false upon login
            this.userLoggedOut = false;
            logger_2.logger.setContextValue(logger_1.contextValues.USER, {
                id: user.id,
                companyCode: (0, guards_1.isExpertUser)(user) ? user.companyCode : undefined,
            });
            logger_2.logger.info('User logged in', user.id);
        }
        // User Logging out
        if (!user && this._authUser) {
            logger_2.logger.info('User logged out', this._authUser.id);
            logger_2.logger.removeContextValue(logger_1.contextValues.USER);
        }
        this._authUser = user;
        this.authCallbacks.forEach(callback => callback(user, error));
        this.latestAuthResponse = [user, error];
    }
    getLoginConfig(companyCode) {
        return __awaiter(this, void 0, void 0, function* () {
            let config = this.configCache.get(companyCode);
            if (!config) {
                const response = yield fetch(`${this.globalApiUrl}/v1/companies/${companyCode}/public-login-configuration`);
                config = (yield response.json());
                this.configCache.set(companyCode, config);
            }
            return config;
        });
    }
    // specifically get login config for Centralized Login App
    getCentralLoginConfig() {
        return __awaiter(this, void 0, void 0, function* () {
            let config = this.centralLoginConfigCache;
            if (!config) {
                const response = yield fetch(`${this.globalApiUrl}/v1/central-login/public-login-configuration`);
                config = (yield response.json());
                this.centralLoginConfigCache = config;
            }
            return config;
        });
    }
}
__decorate([
    mobx_1.observable
], StreemAuth.prototype, "initialized", void 0);
class StreemAuthTest extends StreemAuth {
}
exports.StreemAuthTest = StreemAuthTest;
// This is a singleton, default export is valid here.
// eslint-disable-next-line import/no-default-export
exports.default = new StreemAuth();
//# sourceMappingURL=streem_auth.js.map